import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, rest } from '@/lib/supabase';
import { queryKeys } from '@/lib/queryClient';
import type { Media } from '@/types/database';

export function useMedia(folder?: string) {
  return useQuery({
    queryKey: queryKeys.media.list(folder),
    queryFn: async () => {
      let query = rest.from<Media>('media')
        .select('*')
        .order('created_at', { ascending: false });

      if (folder) {
        query = query.eq('folder', folder);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Media[];
    },
  });
}

export function useUploadMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      folder = 'general',
      altText,
    }: {
      file: File;
      folder?: string;
      altText?: string;
    }) => {
      // Generate unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${folder}/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('media')
        .getPublicUrl(filePath);

      // Save to database
      const { data, error: dbError } = await supabase
        .from('media')
        .insert({
          file_name: file.name,
          file_path: filePath,
          file_type: file.type,
          file_size: file.size,
          public_url: urlData.publicUrl,
          alt_text: altText || file.name,
          folder,
        } as any)
        .select()
        .single();

      if (dbError) throw dbError;
      return data as Media;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.media.all });
    },
  });
}

export function useUpdateMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Media> & { id: string }) => {
      const { data, error } = await supabase
        .from('media')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Media;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.media.all });
    },
  });
}

export function useDeleteMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, filePath }: { id: string; filePath: string }) => {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('media')
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('media')
        .delete()
        .eq('id', id);

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.media.all });
    },
  });
}
